import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const SmallMultiple = ({ data, metric, color, title }) => {
  // Get the latest data point for the reference line.
  const latestData = data.length > 0 ? data[data.length - 1] : null;
  const latestValue = latestData ? Number(latestData[metric]) : 0;

  // Create a gradient ID based on the metric name.
  const gradientId = `color-${metric.replace(/\s+/g, "_")}`;

  // Custom tooltip component with enforced white background.
  // It displays both the percentage value and the raw count.
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const percentage = dataPoint[metric];
      const rawCount = dataPoint[`${metric}_count`];
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "0.75rem",
            border: "1px solid #e5e7eb",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: 500, color: "#374151" }}>
            {dataPoint.date}
          </p>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "#4B5563" }}>
            {`${title}: ${percentage}% (${rawCount})`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Function to generate a lighter color for the fill.
  const getLighterColor = (hexColor, opacity) => {
    if (hexColor.startsWith("#")) {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return hexColor;
  };

  const fillColor = getLighterColor(color, 0.2);

  // Check if the metric exists in the data.
  const isMetricValid = data.some((d) => d[metric] !== undefined);

  // Format the X-axis value.
  const formatXAxis = (value) => {
    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime()) ? parsedDate.getFullYear() : value;
  };

  // Determine max tick value from data.
  const maxDataValue = data.length
    ? Math.max(...data.map((d) => Number(d[metric]) || 0))
    : 0;
  const maxTick = maxDataValue > 0 ? Math.ceil(maxDataValue / 100) * 100 : 300;
  const ticks = Array.from({ length: maxTick / 100 + 1 }, (_, i) => i * 100);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div style={{ textAlign: "center", width: "100%" }} className="mb-2 text-gray-600">
        <strong>{title}</strong>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={color} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.1} stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            tick={{ fontSize: 10, fill: "#6b7280" }}
            tickMargin={10}
            tickLine={false}
            axisLine={{ stroke: "#f3f4f6" }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#6b7280" }}
            tickLine={false}
            axisLine={false}
            domain={[0, maxTick]}
            ticks={ticks}
            tickFormatter={(val) => Number(val.toFixed(4))}
          />
          <Tooltip content={<CustomTooltip />} />
          {isMetricValid && (
            <Area
              type="monotone"
              dataKey={metric}
              stroke={color}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#${gradientId})`}
            />
          )}
          {latestData && (
            <ReferenceLine
              y={latestValue}
              stroke={color}
              strokeDasharray="3 3"
              strokeOpacity={0.7}
              label={{
                value: `${latestValue}`,
                fill: color,
                fontSize: 10,
                position: "right",
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SmallMultiple;
